/* Grail Nation — Saves + Wants (GN 2.0, Oct 23 2026)
   Guests: saves are kept on this device (localStorage), keyed by product handle.
   Logged-in customers: saves sync to their Shopify account (customer metafield grailnation.wants) through the
   app proxy /apps/gn/wants, shared with the phone app. A guest's saves merge into the account at login.
   Card heart sits beside the product name, below the photo. */
(() => {
  if (window.GNWants) return;
  const GUEST_KEY = 'gn_wants_v1';
  const ACCT_KEY = 'gn_wants_acct_v1';
  const API = '/apps/gn/wants';
  let useAccount = !!window.GN_WANTS_CUSTOMER;
  let KEY = useAccount ? ACCT_KEY : GUEST_KEY;
  const ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const WANTS_URL = ROOT + 'pages/wants';
  const HEART = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>';

  const readKey = (k) => {
    try {
      const v = JSON.parse(localStorage.getItem(k) || '[]');
      return Array.isArray(v) ? v.filter((x) => x && typeof x.h === 'string') : [];
    } catch (e) { return []; }
  };
  const read = () => readKey(KEY);
  const write = (list) => { try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {} };
  const has = (h) => read().some((x) => x.h === h);

  // ── Account sync (logged-in customers) ──
  function api(method, body) {
    return fetch(API, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((r) => {
      if (!r.ok) throw new Error('wants ' + r.status);
      return r.json();
    }).then((d) => (d.items || []).map((i) => i && i.handle).filter(Boolean));
  }

  function setHandles(handles) {
    const old = new Map(read().map((x) => [x.h, x.t]));
    const now = Date.now();
    write(handles.slice(0, 200).map((h, i) => ({ h, t: old.get(h) || now - i })));
    refresh();
    renderWants();
    renderCartWants();
  }

  async function syncAccount() {
    if (!useAccount) return;
    const guest = readKey(GUEST_KEY).map((x) => x.h);
    try {
      const items = await (guest.length ? api('POST', { action: 'merge', handles: guest }) : api('GET'));
      if (guest.length) { try { localStorage.setItem(GUEST_KEY, '[]'); } catch (e) {} }
      setHandles(items);
    } catch (e) {
      // Account sync unavailable: keep working from this device.
      const merged = readKey(ACCT_KEY);
      readKey(GUEST_KEY).forEach((x) => { if (!merged.some((m) => m.h === x.h)) merged.push(x); });
      useAccount = false;
      KEY = GUEST_KEY;
      write(merged);
      refresh();
      renderWants();
    }
  }

  function toggle(h) {
    const before = read();
    const list = before.slice();
    const i = list.findIndex((x) => x.h === h);
    let saved;
    if (i > -1) { list.splice(i, 1); saved = false; }
    else { list.unshift({ h, t: Date.now() }); saved = true; }
    write(list);
    refresh();
    document.dispatchEvent(new CustomEvent('gn:wants-change', { detail: { handle: h, saved, count: list.length } }));
    if (useAccount) {
      api('POST', { action: saved ? 'add' : 'remove', handle: h }).then((items) => {
        // Keep this tap's result even if the server list arrives a moment later.
        const merged = items.filter((x) => saved || x !== h);
        if (saved && merged.indexOf(h) < 0) merged.unshift(h);
        setHandles(merged);
      }).catch(() => {
        write(before);
        refresh();
        renderWants();
        toastText('Couldn’t save that. Please try again.');
      });
    }
    return saved;
  }

  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const handleFromHref = (href) => {
    if (!href) return null;
    const m = href.match(/\/products\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  };
  const money = (cents) => {
    const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(cents / 100); }
    catch (e) { return '$' + (cents / 100).toFixed(2); }
  };
  const priceRange = (p) => { const a = (p.variants || []).filter((v) => v.available).map((v) => v.price).sort((x, y) => x - y); const lo = a.length ? a[0] : p.price; const hi = a.length ? a[a.length - 1] : p.price; return money(lo) + (hi > lo ? '–' + money(hi) : ''); }; const imgUrl = (src, w) => {
    if (!src) return '';
    const s = src.startsWith('//') ? 'https:' + src : src;
    return s + (s.includes('?') ? '&' : '?') + 'width=' + w;
  };

  function iconButton(h, title) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gn-save gn-save--icon';
    b.dataset.gnSave = h;
    b.setAttribute('aria-pressed', 'false');
    b.setAttribute('aria-label', 'Save ' + (title || 'this pair') + ' to Watchlist');
    b.innerHTML = HEART;
    return b;
  }

  function fullButton(h) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'gn-save gn-save--full';
    b.dataset.gnSave = h;
    b.setAttribute('aria-pressed', 'false');
    b.innerHTML = HEART + '<span data-gn-save-label>Save to Watchlist</span>';
    return b;
  }

  function decorateCards(root) {
    (root || document).querySelectorAll('hdt-card-product, .hdt-card-product').forEach((card) => {
      const media = card.querySelector('.hdt-card-product__content') || card.querySelector('.hdt-card-product__media');
      if (!media || card.querySelector('.gn-save')) return;
      const link = card.querySelector('a[data-pr-url], a[href*="/products/"]');
      const h = handleFromHref(link && link.getAttribute('href'));
      if (!h) return;
      const title = (card.querySelector('.hdt-card-product__title a') || {}).textContent;
      const btn = iconButton(h, title && title.trim());
      btn.classList.add('gn-save--card');
      media.classList.add('gn-has-save');
      media.appendChild(btn);
    });
  }

  function decorateProductPage() {
    const h = window.GN_PRODUCT_HANDLE;
    if (!h || document.querySelector('.gn-save--full')) return;
    const info = document.querySelector('#MainContent .hdt-product__info-container');
    if (!info) return;
    const add = info.querySelector('button[name="add"]');
    let anchor = add;
    while (anchor && anchor.parentElement !== info) anchor = anchor.parentElement;
    const wrap = document.createElement('div');
    wrap.className = 'gn-save-wrap hdt-product-info__item';
    wrap.appendChild(fullButton(h));
    if (anchor) anchor.insertAdjacentElement('afterend', wrap);
    else info.appendChild(wrap);
  }

  function decorateHeader() {
    let added = false;
    document.querySelectorAll('.gxh__tools, .hdt-header__utilities').forEach((u) => {
      if (u.querySelector('.gn-wants-link')) return;
      const gx = u.classList.contains('gxh__tools');
      const a = document.createElement('a');
      a.href = WANTS_URL;
      a.className = 'gn-wants-link hdt-relative' + (gx ? ' gxh__icon' : '');
      a.setAttribute('aria-label', 'Watchlist');
      a.innerHTML = HEART + '<span class="gn-wants-count" data-gn-wants-count hidden>0</span>';
      const cart = u.querySelector('.gxh__cart, .hdt-cart-icon');
      if (cart) u.insertBefore(a, cart); else u.appendChild(a);
      added = true;
    });
    if (added) { try { window.dispatchEvent(new Event('resize')); } catch (e) {} }
  }

  function refresh() {
    const set = new Set(read().map((x) => x.h));
    document.querySelectorAll('[data-gn-save]').forEach((b) => {
      const on = set.has(b.dataset.gnSave);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      b.classList.toggle('is-saved', on);
      const label = b.querySelector('[data-gn-save-label]');
      if (label) label.textContent = on ? 'Saved' : 'Save to Watchlist';
    });
    document.querySelectorAll('[data-gn-wants-count]').forEach((el) => {
      el.textContent = set.size > 99 ? '99+' : String(set.size);
      el.hidden = set.size === 0;
    });
  }

  let toastEl, toastTimer;
  function showToast(html) {
    if (!toastEl) {
      toastEl = document.createElement('div');
      toastEl.className = 'gn-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      document.body.appendChild(toastEl);
    }
    toastEl.innerHTML = html;
    toastEl.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-on'), 2600);
  }
  function toast(saved) {
    showToast(saved
      ? '<span>Saved to Watchlist</span><a href="' + WANTS_URL + '">View</a>'
      : '<span>Removed from Watchlist</span>');
  }
  function toastText(msg) { showToast('<span>' + esc(msg) + '</span>'); }

  const cache = new Map();
  async function fetchProduct(h) {
    if (cache.has(h)) return cache.get(h);
    let out;
    try {
      const r = await fetch(ROOT + 'products/' + encodeURIComponent(h) + '.js', { headers: { Accept: 'application/json' } });
      out = r.status === 404 ? { gone: true } : r.ok ? { p: await r.json() } : { err: true };
    } catch (e) { out = { err: true }; }
    if (!out.err) cache.set(h, out);
    return out;
  }

  function sizesOf(p) {
    const idx = (p.options || []).findIndex((o) => /size/i.test(typeof o === 'string' ? o : o.name));
    if (idx < 0) return [];
    const key = 'option' + (idx + 1);
    const seen = new Set();
    (p.variants || []).forEach((v) => { if (v.available && v[key]) seen.add(v[key]); });
    return [...seen];
  }

  function cardHTML(p) {
    const url = ROOT + 'products/' + p.handle;
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const sizes = sizesOf(p);
    return (
      '<article class="gn-want' + (p.available ? '' : ' is-soldout') + '">' +
        '<a class="gn-want__media" href="' + url + '">' +
          (p.featured_image ? '<img src="' + esc(imgUrl(p.featured_image, 600)) + '" alt="' + esc(p.title) + '" loading="lazy" width="600" height="600">' : '') +
          (p.available ? '' : '<span class="gn-want__badge">Sold out</span>') +
          (p.available && onSale ? '<span class="gn-want__badge gn-want__badge--sale">Sale</span>' : '') +
        '</a>' +
        '<button type="button" class="gn-save gn-save--icon is-saved" aria-pressed="true" data-gn-save="' + esc(p.handle) + '" aria-label="Remove ' + esc(p.title) + ' from Watchlist">' + HEART + '</button>' +
        '<div class="gn-want__body">' +
          (p.vendor ? '<p class="gn-want__brand">' + esc(p.vendor) + '</p>' : '') +
          '<a class="gn-want__title" href="' + url + '">' + esc(p.title) + '</a>' +
          '<p class="gn-want__price">' +
            '<span class="' + (onSale ? 'is-sale' : '') + '">' + priceRange(p) + '</span>' +
            (onSale ? '<s>' + money(p.compare_at_price) + '</s>' : '') +
          '</p>' +
          (sizes.length ? '<p class="gn-want__sizes"><span>In stock:</span> ' + sizes.map((s) => '<span class="gn-chip">' + esc(s) + '</span>').join('') + '</p>' : '') +
          '<a class="gn-btn gn-btn--solid" href="' + url + '">' + (p.available ? 'Shop now' : 'View pair') + '</a>' +
        '</div>' +
      '</article>'
    );
  }

  let rendering = false, renderAgain = false;
  async function renderWants() {
    const grid = document.getElementById('gn-wants-grid');
    if (!grid) return;
    if (rendering) { renderAgain = true; return; }
    rendering = true;
    const empty = document.getElementById('gn-wants-empty');
    const total = document.getElementById('gn-wants-total');
    const list = read();
    if (total) total.textContent = String(list.length);
    if (!list.length) {
      grid.innerHTML = '';
      if (empty) empty.hidden = false;
    } else {
      if (empty) empty.hidden = true;
      if (!grid.children.length) grid.innerHTML = list.map(() => '<div class="gn-want gn-want--skeleton"></div>').join('');
      grid.setAttribute('aria-busy', 'true');
      const results = await Promise.all(list.map((x) => fetchProduct(x.h).then((r) => ({ h: x.h, ...r }))));
      // Products that no longer exist are hidden here; the account list itself is left alone.
      if (!useAccount) {
        const gone = results.filter((r) => r.gone).map((r) => r.h);
        if (gone.length) write(read().filter((x) => !gone.includes(x.h)));
      }
      const items = results.filter((r) => r.p);
      grid.innerHTML = items.map((r) => cardHTML(r.p)).join('');
      grid.removeAttribute('aria-busy');
      if (total) total.textContent = String(items.length);
      if (!items.length && empty) empty.hidden = false;
    }
    rendering = false;
    refresh();
    if (renderAgain) { renderAgain = false; renderWants(); }
  }


  // ── Cart page: "From your Watchlist" ──
  // Saved pairs that are in stock and not already in the bag, with a size picker and Add to bag.
  const isCartPage = () => /\/cart\/?$/.test(location.pathname);
  let cartTimer;
  function renderCartWants() {
    if (!isCartPage()) return;
    clearTimeout(cartTimer);
    cartTimer = setTimeout(drawCartWants, 150);
  }
  let cartSeq = 0;
  async function drawCartWants() {
    const seq = ++cartSeq;
    const anchor = document.querySelector('cart-items') || document.querySelector('#MainContent .shopify-section');
    const host = anchor && (anchor.closest('.shopify-section') || anchor);
    if (!host) return;
    const list = read();
    let inCart = new Set();
    let products = [];
    if (list.length) {
      try {
        const cart = await fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
        inCart = new Set((cart.items || []).map((i) => i.handle));
      } catch (e) {}
      const handles = list.map((x) => x.h).filter((h) => !inCart.has(h)).slice(0, 12);
      const results = await Promise.all(handles.map((h) => fetchProduct(h)));
      products = results.map((r) => r && r.p).filter((p) => p && p.available);
    }
    if (seq !== cartSeq) return; // a newer render took over while this one was loading
    let box = document.getElementById('gn-cart-wants');
    if (!products.length) { if (box) box.remove(); return; }
    if (!box) {
      box = document.createElement('section');
      box.id = 'gn-cart-wants';
      box.className = 'gn-cart-wants';
      host.insertAdjacentElement('afterend', box);
    }
    box.innerHTML =
      '<div class="gn-cart-wants__head"><div><span class="gn-cart-wants__idx">// From your Watchlist</span>' +
      '<h2 class="gn-cart-wants__title">Still on your list</h2>' +
      '<p class="gn-cart-wants__sub">Pairs you saved that are still in stock. Add them before they&rsquo;re gone.</p></div>' +
      '<a class="gn-cart-wants__all" href="' + WANTS_URL + '">View Watchlist &rarr;</a></div>' +
      '<div class="gn-cart-wants__grid">' + products.map(cartCardHTML).join('') + '</div>';
    refresh();
  }
  function cartCardHTML(p) {
    const url = ROOT + 'products/' + p.handle;
    const idx = (p.options || []).findIndex((o) => /size/i.test(typeof o === 'string' ? o : o.name));
    const key = idx > -1 ? 'option' + (idx + 1) : null;
    const variants = (p.variants || []).filter((v) => v.available);
    const label = (v) => (key && v[key]) || v.title;
    const onSale = p.compare_at_price && p.compare_at_price > p.price;
    const picker = variants.length > 1
      ? '<select class="gn-cart-want__size" aria-label="Size for ' + esc(p.title) + '">' +
          variants.map((v) => '<option value="' + v.id + '">' + esc(label(v)) + ' &middot; ' + money(v.price) + '</option>').join('') +
        '</select>'
      : '<input type="hidden" class="gn-cart-want__size" value="' + variants[0].id + '">' +
        (label(variants[0]) && label(variants[0]) !== 'Default Title' ? '<span class="gn-cart-want__one">Size ' + esc(label(variants[0])) + '</span>' : '');
    return (
      '<article class="gn-cart-want">' +
        '<a class="gn-cart-want__media" href="' + url + '">' +
          (p.featured_image ? '<img src="' + esc(imgUrl(p.featured_image, 400)) + '" alt="' + esc(p.title) + '" loading="lazy" width="400" height="400">' : '') +
        '</a>' +
        '<button type="button" class="gn-save gn-save--icon is-saved" aria-pressed="true" data-gn-save="' + esc(p.handle) + '" aria-label="Remove ' + esc(p.title) + ' from Watchlist">' + HEART + '</button>' +
        '<div class="gn-cart-want__body">' +
          (p.vendor ? '<p class="gn-want__brand">' + esc(p.vendor) + '</p>' : '') +
          '<a class="gn-cart-want__title" href="' + url + '">' + esc(p.title) + '</a>' +
          '<p class="gn-want__price"><span class="' + (onSale ? 'is-sale' : '') + '">' + priceRange(p) + '</span>' +
            (onSale ? '<s>' + money(p.compare_at_price) + '</s>' : '') + '</p>' +
          picker +
          '<button type="button" class="gn-btn gn-btn--solid gn-cart-want__add" data-gn-cart-add>Add to bag</button>' +
        '</div>' +
      '</article>'
    );
  }
  document.addEventListener('click', async (e) => {
    const b = e.target.closest('[data-gn-cart-add]');
    if (!b) return;
    e.preventDefault();
    const card = b.closest('.gn-cart-want');
    const id = card && card.querySelector('.gn-cart-want__size') && card.querySelector('.gn-cart-want__size').value;
    if (!id) return;
    b.disabled = true;
    b.textContent = 'Adding…';
    try {
      const r = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] }),
      });
      if (!r.ok) throw new Error('add ' + r.status);
      location.reload();
    } catch (err) {
      b.disabled = false;
      b.textContent = 'Add to bag';
      toastText('Couldn’t add that pair. It may have just sold.');
    }
  });

  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-gn-save]');
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    toast(toggle(b.dataset.gnSave));
  }, true);

  document.addEventListener('gn:wants-change', () => {
    if (document.getElementById('gn-wants-grid')) renderWants();
    renderCartWants();
  });

  window.addEventListener('storage', (e) => {
    if (e.key !== KEY) return;
    refresh();
    renderWants();
  });

  let moTimer;
  const mo = new MutationObserver(() => {
    clearTimeout(moTimer);
    moTimer = setTimeout(() => { decorateCards(); decorateHeader(); refresh(); }, 120);
  });

  function init() {
    decorateHeader();
    decorateCards();
    decorateProductPage();
    refresh();
    renderWants();
    renderCartWants();
    mo.observe(document.body, { childList: true, subtree: true });
    syncAccount();
  }

  window.GNWants = { list: () => read().map((x) => x.h), has, toggle, refresh, sync: syncAccount, account: () => useAccount };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
